import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import {
  ActionSheet,
  DropdownMenu,
  IconButton,
  Icons,
  List,
  Typography,
  useAlertContext,
} from '@usepepper/sprout-kit-ui';
import { AgentThreadRenameDialog } from '@/experiences/sales-portal/domain/superintelligence/components/agent-thread-rename-dialog';
import { GestureResponderEvent } from 'react-native';
import { isWeb } from '@/utils/navigation';
import { useAui } from '@assistant-ui/store';
import { useTheme } from '@/contexts/theme-context';

const ICON_SIZE = 16;
const MENU_MIN_WIDTH = 180;

interface ThreadAction {
  destructive?: boolean;
  key: string;
  label: string;
  onPress: () => void;
}

interface AgentThreadRowActionsProps {
  archived: boolean;
  inRow?: boolean;
  onOpenChange?: (open: boolean) => void;
  threadId: string;
  title: string | null;
}

export const AgentThreadRowActions: React.FC<AgentThreadRowActionsProps> = ({
  archived,
  inRow = false,
  onOpenChange,
  threadId,
  title,
}) => {
  const theme = useTheme();
  const aui = useAui();
  const { alert } = useAlertContext('superintelligence_thread');
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);

  const changeMenu = (open: boolean) => {
    setMenuOpen(open);
    onOpenChange?.(open);
  };

  const item = () => aui.threads.item({ id: threadId });

  const actions: ThreadAction[] = [
    {
      key: 'rename',
      label: i18n.t('rename'),
      onPress: () => setRenaming(true),
    },
    archived
      ? {
          key: 'unarchive',
          label: i18n.t('sales_portal.superintelligence.unarchive'),
          onPress: () => item().unarchive(),
        }
      : {
          key: 'archive',
          label: i18n.t('sales_portal.superintelligence.archive'),
          onPress: () => item().archive(),
        },
    {
      destructive: true,
      key: 'delete',
      label: i18n.t('delete'),
      onPress: () =>
        alert({
          buttons: [
            { action: () => {}, style: 'cancel', title: i18n.t('cancel') },
            {
              action: () => item().delete(),
              style: 'destructive',
              title: i18n.t('delete'),
            },
          ],
          message: i18n.t(
            'sales_portal.superintelligence.delete_confirm_message',
          ),
          title: i18n.t('sales_portal.superintelligence.delete_confirm_title'),
        }),
    },
  ];

  const run = (
    event: GestureResponderEvent | undefined,
    action: ThreadAction,
  ) => {
    event?.stopPropagation();
    changeMenu(false);
    action.onPress();
  };

  const toggleMenu = (event?: GestureResponderEvent) => {
    event?.stopPropagation();
    changeMenu(!menuOpen);
  };
  const icon = (
    <Icons.Ellipsis color={theme.sk.color.icon.secondary} size={ICON_SIZE} />
  );
  const trigger =
    inRow && isWeb ? (
      <List.Item.Action
        aria-label={i18n.t('more_actions')}
        onPress={toggleMenu}
      >
        {icon}
      </List.Item.Action>
    ) : (
      <IconButton
        aria-label={i18n.t('more_actions')}
        icon={icon}
        onPress={toggleMenu}
        shape="rounded"
        size="$sm"
        variant="tertiary"
      />
    );

  return (
    <>
      {isWeb ? (
        <DropdownMenu
          onOpenChange={changeMenu}
          open={menuOpen}
          placement="bottom-end"
        >
          <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
          <DropdownMenu.Content
            autoFocusFirstItem={false}
            minWidth={MENU_MIN_WIDTH}
          >
            {actions.map((action) => (
              <DropdownMenu.Item
                key={action.key}
                onPress={(event?: GestureResponderEvent) => run(event, action)}
              >
                <Typography
                  color={action.destructive ? '$textError' : '$textPrimary'}
                  size="$sm"
                  textAlign="left"
                >
                  {action.label}
                </Typography>
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu>
      ) : (
        <>
          {trigger}
          <ActionSheet onOpenChange={changeMenu} open={menuOpen} size="fit">
            {menuOpen ? (
              <>
                <ActionSheet.Header>
                  <ActionSheet.Title numberOfLines={1}>
                    {title ??
                      i18n.t('sales_portal.superintelligence.new_conversation')}
                  </ActionSheet.Title>
                </ActionSheet.Header>
                <ActionSheet.Body>
                  <List>
                    {actions.map((action) => (
                      <List.Item
                        key={action.key}
                        onPress={(event?: GestureResponderEvent) =>
                          run(event, action)
                        }
                        pressable
                      >
                        <List.Item.Content>
                          <List.Item.Title
                            color={
                              action.destructive ? '$textError' : '$textPrimary'
                            }
                          >
                            {action.label}
                          </List.Item.Title>
                        </List.Item.Content>
                      </List.Item>
                    ))}
                  </List>
                </ActionSheet.Body>
              </>
            ) : null}
          </ActionSheet>
        </>
      )}
      <AgentThreadRenameDialog
        onOpenChange={setRenaming}
        onSubmit={(nextTitle) => item().rename(nextTitle)}
        open={renaming}
        title={title}
      />
    </>
  );
};
